/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {hexToRgb} from '@ion/core/src/utils/StringUtil';
import {dlsColor} from '../../../theme/theme';
import {AlertIndicatorValues, AssetAlertIndicators, hasActiveAlerts} from './AssetAlertIndicators';
import {AssetHealthIndicators, hasHealthIndicators, HealthIndicatorValues} from './AssetHealthIndicators';
import {VehicleFuelType} from './vehicleTypeIndicatorConfig';

interface AssetSummaryCardDiagnosticsProps {
  vehicleType: VehicleFuelType;
  healthValues: HealthIndicatorValues;
  alertValues: AlertIndicatorValues;
}

const useStyles = makeStyles(() => ({
  diagnostics : {
    display                 : 'flex',
    width                   : '100%',
    minWidth                : 0,
    padding                 : '8px 4px',
    columnGap               : '8px',
    // per Figma, the bar itself is `align-items: flex-start` (its declared
    // height, 40px, is fixed padding + content, not a stretch target) - only
    // the divider individually opts back into `align-self: stretch` so it
    // still spans the Health section's full height when Health wraps ...
    alignItems              : 'flex-start',
    borderTop               : `1px solid ${dlsColor('grey5')}`,
    backgroundColor         : `rgba(${hexToRgb(dlsColor('grey8')).join(', ')}, 0.8)`,
    borderBottomLeftRadius  : '8px',
    borderBottomRightRadius : '8px'
  },
  // the divider and Alerts, kept at the bar's right end whether or not
  // Health indicators fill the space before them ...
  alertsGroup : {
    display    : 'flex',
    alignSelf  : 'stretch',
    alignItems : 'flex-start',
    columnGap  : '8px',
    flexShrink : 0,
    marginLeft : 'auto'
  },
  divider : {
    width           : '1px',
    alignSelf       : 'stretch',
    flexShrink      : 0,
    backgroundColor : dlsColor('grey5')
  }
}));

/**
 * AssetSummaryCardDiagnostics renders the card's Diagnostic Summary strip:
 * Health indicators (left, wraps) and Alerts (right, never wraps),
 * separated by a vertical divider. Per ION-7392/ION-7397, the divider (and
 * the entire Alerts section) is hidden when there are no active alerts, and
 * otherwise stretches to match the Health section's height when it wraps
 * onto additional rows - `alignSelf: stretch` on a flex row does this for
 * free, without measuring anything. The whole bar (background, border,
 * padding included) renders nothing at all when there is neither a Health
 * indicator nor an active Alert to show, rather than leaving an empty band.
 *
 * ```typescript
 *
 *   import {AssetSummaryCardDiagnostics} from
 *     '@ion/dls/src/components/assetcard/current/AssetSummaryCardDiagnostics';
 *   ...
 *
 *   <AssetSummaryCardDiagnostics vehicleType="gas"
 *     healthValues={{seatbelt: {value: 'Buckled'}}}
 *     alertValues={{gpsQuality: {value: 'Poor'}}} />
 *
 * ```
 *
 */
export const AssetSummaryCardDiagnostics = ({
  vehicleType,
  healthValues,
  alertValues
}: AssetSummaryCardDiagnosticsProps) => {
  const classes     = useStyles();
  const showHealth  = hasHealthIndicators(vehicleType, healthValues);
  const showAlerts  = hasActiveAlerts(vehicleType, alertValues);
  let diagnostics: React.ReactElement = null;

  if (showHealth || showAlerts) {
    diagnostics = (
      <div className={classes.diagnostics}>
        {showHealth && (
          <AssetHealthIndicators vehicleType={vehicleType} values={healthValues} />
        )}
        {showAlerts && (
          <div className={classes.alertsGroup}>
            <div className={classes.divider} />
            <AssetAlertIndicators vehicleType={vehicleType} values={alertValues} />
          </div>
        )}
      </div>
    );
  }

  return diagnostics;
};

export default AssetSummaryCardDiagnostics;
